import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface LawData {
  id: number;
  name: string;
  nameEn: string;
  icon: string;
  formula: string;
  description: string;
  example: string;
  color: string;
}

const LAWS: LawData[] = [
  {
    id: 0,
    name: '热力学第零定律',
    nameEn: 'Zeroth Law',
    icon: '🌡️',
    formula: 'A=B, B=C → A=C',
    description: '热平衡的传递性：如果A与B热平衡，B与C热平衡，则A与C也热平衡',
    example: '温度计原理——水银与被测物体达到热平衡',
    color: '#4ECDC4',
  },
  {
    id: 1,
    name: '热力学第一定律',
    nameEn: 'First Law',
    icon: '🔥',
    formula: 'ΔU = Q − W',
    description: '能量守恒：系统内能变化等于吸收热量减去对外做功',
    example: '汽车发动机将燃料化学能转化为动能和热能',
    color: '#FF6B35',
  },
  {
    id: 2,
    name: '热力学第二定律',
    nameEn: 'Second Law',
    icon: '♻️',
    formula: 'ΔS ≥ 0',
    description: '熵增原理：孤立系统的熵永远不会自发减小',
    example: '冰块在室温下融化——热量自发从高温传向低温',
    color: '#667EEA',
  },
  {
    id: 3,
    name: '热力学第三定律',
    nameEn: 'Third Law',
    icon: '❄️',
    formula: 'T→0K, S→0',
    description: '绝对零度不可达：温度趋近绝对零度时熵趋近于零',
    example: '超导材料需要极低温但永远无法达到0K',
    color: '#A78BFA',
  },
];

const Particle: React.FC<{
  x: number;
  y: number;
  size: number;
  speed: number;
  frame: number;
  color: string;
}> = ({ x, y, size, speed, frame, color }) => {
  const px = x + Math.sin(frame * speed * 0.05 + y) * 30;
  const py = y + Math.cos(frame * speed * 0.04 + x) * 20;
  return (
    <div
      style={{
        position: 'absolute',
        left: px,
        top: py,
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        opacity: 0.6 + Math.sin(frame * 0.08 + x) * 0.3,
      }}
    />
  );
};

export const ThermodynamicsAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });
  const titleY = interpolate(titleProg, [0, 1], [-40, 0]);

  const currentLawIndex = Math.min(
    Math.floor(Math.max(0, frame - 60) / 110),
    LAWS.length - 1,
  );

  const particles = React.useMemo(() => {
    const out: { x: number; y: number; s: number; spd: number; c: string }[] = [];
    for (let i = 0; i < 40; i++) {
      const h1 = ((i * 7919 + 104729) % 100000) / 100000;
      const h2 = ((i * 6271 + 73849) % 100000) / 100000;
      out.push({
        x: h1 * 1920,
        y: h2 * 1080,
        s: 3 + h1 * 4,
        spd: 0.5 + h2 * 2,
        c: ['#FF6B35', '#4ECDC4', '#667EEA', '#A78BFA'][i % 4],
      });
    }
    return out;
  }, []);

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 40% 50%, #1a1a2e 0%, #0a0a1a 60%, #050510 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      {particles.map((p, i) => (
        <Particle key={i} x={p.x} y={p.y} size={p.s} speed={p.spd} frame={frame} color={p.c} />
      ))}

      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 60,
          opacity: titleProg,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 72,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #FF6B35, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          热力学四大定律
        </h1>
        <p style={{ fontSize: 30, margin: '8px 0 0', color: 'rgba(255,255,255,0.45)' }}>
          Laws of Thermodynamics · 能量与熵的宇宙法则
        </p>
      </div>

      {LAWS.map((law, i) => {
        const phaseStart = 60 + i * 110;
        const phaseEnd = phaseStart + 105;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 20], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 15, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === LAWS.length - 1 ? fadeIn : fadeIn * fadeOut;
        const slideX = interpolate(fadeIn, [0, 1], [60, 0]);

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={law.id}
            style={{
              position: 'absolute',
              top: 330,
              left: 60,
              right: 60,
              opacity,
              transform: `translateX(${slideX}px)`,
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 50,
                alignItems: 'flex-start',
              }}
            >
              <div
                style={{
                  width: 500,
                  padding: '40px 44px',
                  background: `linear-gradient(135deg, ${law.color}18, ${law.color}08)`,
                  border: `2px solid ${law.color}40`,
                  borderRadius: 24,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                  <span style={{ fontSize: 56 }}>{law.icon}</span>
                  <div>
                    <h2 style={{ fontSize: 42, fontWeight: 700, color: law.color, margin: 0 }}>
                      {law.name}
                    </h2>
                    <p style={{ fontSize: 30, color: 'rgba(255,255,255,0.4)', margin: '4px 0 0' }}>
                      {law.nameEn}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    padding: '20px 24px',
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: 14,
                    textAlign: 'center',
                    marginBottom: 20,
                  }}
                >
                  <span
                    style={{
                      fontSize: 48,
                      fontWeight: 700,
                      color: '#FFD700',
                      fontFamily: 'Georgia, serif',
                    }}
                  >
                    {law.formula}
                  </span>
                </div>

                <p
                  style={{
                    fontSize: 28,
                    color: '#E5E7EB',
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  {law.description}
                </p>
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    padding: '36px 40px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 20,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <h3 style={{ fontSize: 32, color: law.color, margin: '0 0 16px', fontWeight: 600 }}>
                    💡 生活实例
                  </h3>
                  <p style={{ fontSize: 30, color: '#D1D5DB', lineHeight: 1.7, margin: 0 }}>
                    {law.example}
                  </p>
                </div>

                <EntropyVisualization frame={frame} lawIndex={i} phaseStart={phaseStart} color={law.color} />
              </div>
            </div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 60,
          right: 60,
          display: 'flex',
          justifyContent: 'center',
          gap: 16,
        }}
      >
        {LAWS.map((law, i) => (
          <div
            key={law.id}
            style={{
              width: i === currentLawIndex ? 48 : 14,
              height: 14,
              borderRadius: 7,
              background: i === currentLawIndex ? law.color : 'rgba(255,255,255,0.15)',
              transition: 'all 0.3s',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};

const EntropyVisualization: React.FC<{
  frame: number;
  lawIndex: number;
  phaseStart: number;
  color: string;
}> = ({ frame, lawIndex, phaseStart, color }) => {
  const localFrame = frame - phaseStart;
  const barCount = 8;

  return (
    <div
      style={{
        marginTop: 30,
        padding: '28px 32px',
        background: 'rgba(0,0,0,0.25)',
        borderRadius: 16,
        border: `1px solid ${color}30`,
      }}
    >
      <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>
        {['温度平衡过程', '能量转换分布', '熵值增长曲线', '温度递降趋势'][lawIndex]}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 120 }}>
        {Array.from({ length: barCount }).map((_, i) => {
          const seed = ((i * 3571 + 85691 + lawIndex * 1000) % 100000) / 100000;
          const targetHeight = 30 + seed * 90;
          const barProg = interpolate(localFrame, [10 + i * 5, 30 + i * 5], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                height: targetHeight * barProg,
                background: `linear-gradient(180deg, ${color}, ${color}44)`,
                borderRadius: '6px 6px 0 0',
                opacity: 0.7 + barProg * 0.3,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
